import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ExternalLink, CheckCircle } from 'lucide-react';
import api from '../lib/api';
import { TopBar } from '../components/TopBar';
import { useAuth } from '../contexts/AuthContext';


const platformGuides = {
  etsy: {
    name: 'Etsy',
    url: 'https://www.etsy.com/your/shops/me/listings',
    steps: [
      'Click "Open Etsy Listings" button below',
      'Login to your Etsy account',
      'Open Shop Manager',
      'Go to Listings section',
      'Click "Add Listing" or "Import"',
      'Upload your CSV file',
      'Fill any missing details (category, shipping)',
      'Review and click "Publish"'
    ],
    tips: [
      'Make sure you have an Etsy seller account',
      'Keep your product photos ready',
      'Double-check pricing and shipping options'
    ]
  },
  amazon: {
    name: 'Amazon',
    url: 'https://sellercentral.amazon.in/inventory/upload',
    steps: [
      'Click "Open Amazon Upload" button below',
      'Login to Amazon Seller Central account',
      'Go to Inventory → Add Products via Upload',
      'Select "Upload your inventory file"',
      'Upload your CSV file',
      'Click "Submit"',
      'Wait for processing (5-15 minutes)',
      'Check status in Inventory dashboard'
    ],
    tips: [
      'You need Amazon Seller Central account',
      'Processing may take 10-15 minutes',
      'Check for any error messages after upload'
    ]
  },
  flipkart: {
    name: 'Flipkart',
    url: 'https://seller.flipkart.com/',
    steps: [
      'Click "Open Flipkart Seller Hub" button below',
      'Login to Flipkart Seller account',
      'Go to Listings → Add New Listing',
      'Select "Bulk Upload" option',
      'Upload your CSV file',
      'Fill missing details (HSN code, brand)',
      'Review your listing',
      'Submit for approval'
    ],
    tips: [
      'Flipkart approval can take 24-48 hours',
      'Keep GST details ready',
      'Update HSN code and brand in the file if needed'
    ]
  }
};

export const PlatformGuideScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { platform } = location.state || { platform: 'etsy' };
  const { user } = useAuth();
  
  const guide = platformGuides[platform] || platformGuides.etsy;

  // Track guide view
  useEffect(() => {
    const trackView = async () => {
      try {
        await api.post(
          `/analytics/track`,
          {
            event_type: 'guide_view',
            platform
          }
        );
      } catch (error) {
        console.error('Analytics tracking failed:', error);
      }
    };
    
    if (user?.id) {
      trackView();
    }
  }, [platform, user]);

  const handleOpenPlatform = async () => {
    // Track link click
    try {
      await api.post(
        `/analytics/track`,
        {
          event_type: 'platform_link_click',
          platform
        }
      );
    } catch (error) {
      console.error('Analytics tracking failed:', error);
    }
    
    window.open(guide.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-[#F0F2F5]">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        <TopBar title={`Upload on ${guide.name}`} showBack={true} />

        <div className="p-6 space-y-6">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-2xl font-bold text-slate-800 mb-3">
              How to Upload on {guide.name}
            </h1>
            <p className="text-base text-[#667781] leading-relaxed">
              Follow these simple steps to list your product
            </p>
          </motion.div>

          {/* Steps */}
          <div className="bg-white rounded-2xl border-2 border-gray-200 p-5">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <CheckCircle size={24} className="text-[#128C7E]" />
              Step-by-Step Guide
            </h3>
            <ol className="space-y-4">
              {guide.steps.map((step, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-start gap-4"
                >
                  <div className="flex-shrink-0 w-8 h-8 bg-[#128C7E] text-white rounded-full flex items-center justify-center font-bold">
                    {index + 1}
                  </div>
                  <p className="text-base text-slate-700 leading-relaxed pt-1">
                    {step}
                  </p>
                </motion.li>
              ))}
            </ol>
          </div>

          {/* Open Platform Button */}
          <button
            onClick={handleOpenPlatform}
            className="w-full h-16 bg-[#25D366] text-white rounded-xl text-xl font-bold flex items-center justify-center gap-3 transition-transform active:scale-95 shadow-lg shadow-green-200"
            data-testid="open-platform-button"
          >
            <ExternalLink size={28} />
            Open {guide.name} Upload Page
          </button>

          {/* Tips */}
          <div className="bg-amber-50 rounded-2xl border-2 border-amber-200 p-5">
            <h3 className="text-lg font-bold text-amber-900 mb-3">
              💡 Important Tips
            </h3>
            <ul className="space-y-2">
              {guide.tips.map((tip, index) => (
                <li
                  key={index}
                  className="flex items-start gap-3 text-base text-amber-900"
                >
                  <span className="text-amber-600 mt-1">•</span>
                  <span className="leading-relaxed">{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Help Section */}
          <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
            <p className="text-base text-blue-900 leading-relaxed">
              <strong>Need Help?</strong><br/>
              If you face any issues, contact {guide.name} seller support or check their help center for detailed documentation.
            </p>
          </div>

          {/* Back to Products Button */}
          <button
            onClick={() => navigate('/my-products')}
            className="w-full h-14 bg-white border-2 border-gray-300 text-slate-800 rounded-xl text-lg font-bold active:scale-95 transition-transform"
            data-testid="back-to-products-button"
          >
            Back to My Products
          </button>
        </div>
      </div>
    </div>
  );
};
